/**
 * RacketLabPlayerPreview v3 (Feb 2026 · RACKET-LAB-PLAYER-EQUIPMENT-UX-3).
 *
 * Frontend-only · preview/demo · no backend calls · no DB writes.
 * `Mi equipación` persists locally in `localStorage` for the current
 * browser only — never crosses the network.
 *
 * Player-side Racket Lab area for the VIP demo:
 *   - Tab "Mi equipación": editable list of the player's rackets
 *     (number, internal reference, QR/RFID, model: brand/model/weight/
 *     grip/year — picked from a curated list or typed manually).
 *   - Tab "Mi QR": player QR card with a large modal view.
 *   - Tab "Reserva de encordado": store picker (with `Abrir en Maps`,
 *     clickable phone / email), per-racket service picker (Encordar /
 *     Revisar / Reparar), mode (Standard / Express / Inmediato), date
 *     picker, slot picker, observations, running total.
 *   - Tab "Mis raquetas / tickets": ticket list (reservations, planned
 *     drop-offs, walk-ins) with internal ref + QR/RFID + full model +
 *     strings + tension + status + ETA.
 *
 * All operations are mock. No API call ever leaves the page. Hard
 * blocks unchanged: no public booking, no payments, no real
 * notifications, no App Store, no ConsentGate.
 */
import React, { useEffect, useMemo, useState } from "react";
import { useLang } from "@/i18n";
import {
  Wrench, Store, QrCode, ShieldCheck, X, Calendar as CalIcon, MapPin,
  Phone, Mail, Plus, Trash2, Pencil, Check, ListChecks, Package,
} from "lucide-react";

// -----------------------------------------------------------------------------
// Mock data sources — preview only · never persisted server-side.
// -----------------------------------------------------------------------------
const MOCK_STORES = {
  "shop-madrid-centro": {
    name: "TEAM Lab · Madrid Centro",
    address: "Calle Gran Vía 42, 28013 Madrid",
    phone: "+34 910 000 010",
    email: "madrid.centro@teamlab.example",
    eta: "48h",
    available_days: ["Mié 19", "Jue 20", "Vie 21", "Sáb 22"],
    available_slots: ["10:00", "12:00", "16:00", "18:00"],
  },
  "shop-barcelona-eixample": {
    name: "TEAM Lab · Eixample",
    address: "Passeig de Gràcia 88, 08008 Barcelona",
    phone: "+34 930 000 020",
    email: "bcn.eixample@teamlab.example",
    eta: "72h",
    available_days: ["Jue 20", "Vie 21", "Sáb 22", "Lun 24"],
    available_slots: ["09:30", "11:30", "17:00", "19:00"],
  },
  "shop-valencia-ruzafa": {
    name: "TEAM Lab · Ruzafa",
    address: "Carrer de Sueca 12, 46006 Valencia",
    phone: "+34 960 000 030",
    email: "vlc.ruzafa@teamlab.example",
    eta: "72h",
    available_days: ["Vie 21", "Sáb 22", "Lun 24", "Mar 25"],
    available_slots: ["10:00", "13:00", "17:30", "19:30"],
  },
};

const RACKET_CATALOG = [
  { brand: "Wilson",  model: "Blade 98 v9",        weight_g: 305, grips: ["L1","L2","L3","L4"], year: 2024 },
  { brand: "Wilson",  model: "Pro Staff 97",       weight_g: 315, grips: ["L2","L3","L4"],      year: 2023 },
  { brand: "Babolat", model: "Pure Aero",          weight_g: 300, grips: ["L1","L2","L3","L4"], year: 2023 },
  { brand: "Babolat", model: "Pure Drive",         weight_g: 300, grips: ["L1","L2","L3","L4"], year: 2024 },
  { brand: "Head",    model: "Speed MP",           weight_g: 300, grips: ["L2","L3","L4"],      year: 2022 },
  { brand: "Head",    model: "Radical Pro",        weight_g: 310, grips: ["L2","L3","L4"],      year: 2024 },
  { brand: "Yonex",   model: "VCORE 98",           weight_g: 305, grips: ["L1","L2","L3"],      year: 2023 },
  { brand: "Prince",  model: "Phantom 100",        weight_g: 305, grips: ["L2","L3"],           year: 2022 },
];

const SERVICES = ["encordar", "revisar", "reparar"];
const MODES = ["standard", "express", "inmediato"];
const PRICE = {
  encordar: 22, revisar: 6, reparar: 12,
  mode_multiplier: { standard: 1, express: 1.35, inmediato: 1.8 },
};

const DEFAULT_RACKETS = [
  {
    id: "r1", internal_ref: "Mi Blade #1", qr_short: "QR-BLD1-7H4K", rfid: "",
    brand: "Wilson", model: "Blade 98 v9", weight_g: 305, grip: "L3", year: 2024,
    manual: false,
  },
  {
    id: "r2", internal_ref: "Mi Pure Aero", qr_short: "QR-PRA-9X2M", rfid: "RFID-A1F2",
    brand: "Babolat", model: "Pure Aero", weight_g: 300, grip: "L2", year: 2023,
    manual: false,
  },
];

const MOCK_TICKETS = [
  {
    racket_id: "r1", source: "reservation",
    strings: "Luxilon ALU Power", tension_kg: "23 / 22",
    state_key: "in_progress", eta: "Vie 18:00",
    store: "TEAM Lab · Madrid Centro",
  },
  {
    racket_id: "r2", source: "reservation",
    strings: "Luxilon Big Banger Original", tension_kg: "24 / 23",
    state_key: "ready", eta: "Hoy 19:00",
    store: "TEAM Lab · Madrid Centro",
  },
  {
    racket_id: "walkin-1", source: "walkin",
    internal_ref: "Backup Speed", qr_short: "QR-SPD-3K8P",
    brand: "Head", model: "Speed MP", weight_g: 300, grip: "L3", year: 2022,
    strings: "Solinco Hyper-G", tension_kg: "22 / 21",
    state_key: "intake", eta: "Sáb 12:00",
    store: "TEAM Lab · Eixample",
  },
  {
    racket_id: "planned-1", source: "planned",
    internal_ref: "Pure Drive (planificada)", qr_short: "QR-PRD-PLN",
    brand: "Babolat", model: "Pure Drive", weight_g: 300, grip: "L3", year: 2024,
    strings: "—", tension_kg: "—",
    state_key: "planned", eta: "Sáb 22 · sin cita",
    store: "TEAM Lab · Ruzafa",
  },
];

const STATE_LABELS = {
  es: { intake: "Recibida", in_progress: "En proceso", ready: "Lista para recoger", done: "Entregada", planned: "Planificada (sin cita)" },
  en: { intake: "Received", in_progress: "In progress", ready: "Ready for pickup", done: "Delivered", planned: "Planned (walk-in)" },
};
const STATE_DOT = {
  intake: "bg-amber-500", in_progress: "bg-emerald-500", ready: "bg-blue-500",
  done: "bg-slate-400", planned: "bg-violet-500",
};
const SOURCE_LABELS = {
  es: { reservation: "Reserva", walkin: "Sin cita", planned: "Planificada" },
  en: { reservation: "Reservation", walkin: "Walk-in", planned: "Planned" },
};

// -----------------------------------------------------------------------------
// i18n strings (self-contained for the preview · no global keys leaked).
// Exported so the standalone Mis raquetas page can reuse the same dictionary.
// -----------------------------------------------------------------------------
export const T = {
  es: {
    sectionTitle: "Racket Lab — área del jugador",
    sectionBadge: "Preview controlado",
    sectionNote: "Vista previa para tu perfil de jugador · datos demo, sin reserva real.",

    tabEquipment: "Mi equipación",
    tabMyRackets: "Mis raquetas",
    tabReservation: "Reserva de encordado",
    tabQr: "Mi QR",

    // ---- Mi equipación
    eqTitle: "Mi equipación · Raquetas",
    eqIntro: "Define tu kit. Cada raqueta tiene una referencia interna (renombrable), un código QR o RFID asociado y un modelo (de catálogo o escrito a mano).",
    eqAdd: "Añadir raqueta",
    eqEmpty: "Aún no has añadido raquetas. Pulsa “Añadir raqueta”.",
    eqRacketHeader: "Raqueta",
    eqInternalRef: "Referencia interna",
    eqInternalRefHelp: "Cómo la llamas tú (p. ej. “Mi Blade #1”).",
    eqQr: "QR asociado",
    eqRfid: "RFID asociado",
    eqOptional: "opcional",
    eqModel: "Modelo",
    eqModelManual: "Escribir a mano",
    eqModelFromList: "Elegir del catálogo",
    // MI-RAQUETAS-SELECTOR-OMIT-L1-1 (Feb 2026)
    eqImplicitProductTitle: "Producto = Raqueta (implícito en esta pestaña)",
    eqImplicitProductHelp: "Selecciona Marca → Modelo → Grip → Peso. category_key=\"raqueta\" se mantiene internamente.",
    eqBrand: "Marca",
    eqModelName: "Modelo",
    eqWeight: "Peso (g)",
    eqGrip: "Grip",
    eqYear: "Año",
    eqRename: "Renombrar",
    eqDone: "Hecho",
    eqRemove: "Eliminar",
    eqConfirmRemove: "¿Eliminar esta raqueta de tu equipación?",
    eqCountLabel: "Cantidad de raquetas",
    eqLocalNote: "Los cambios se guardan sólo en este navegador (preview).",

    // ---- Mi QR
    myQrTitle: "Mi QR",
    myQrBody: "Identificación rápida para tu próxima visita. Tu QR no expone datos personales.",
    myQrCta: "Ver mi QR",
    myQrModalClose: "Cerrar",
    myQrModalDesc: "Vista preview. En producción controlada esto se vincula a tu pasaporte TEAM sin exponer PII.",
    myQrScanHint: "El encordador escanea este código para abrir tu ficha en tablet.",

    // ---- Reservation
    storeTitle: "Reserva de encordado",
    storeBody: "Selecciona una tienda para llevar tus raquetas.",
    storeSelectLabel: "Tienda",
    storeSelectPlaceholder: "— Elige una tienda —",
    storeAddress: "Dirección",
    storePhone: "Teléfono",
    storeEmail: "Email",
    storeEta: "Plazo medio",
    storeOpenMaps: "Abrir en Maps",

    racketCount: "Cantidad de raquetas",
    pickRackets: "Seleccionar raquetas",
    pickRacketsHelp: "Elige cuáles llevas. Saca de “Mi equipación”.",
    services: "Servicios",
    servicesEncordar: "Encordar",
    servicesRevisar: "Revisar",
    servicesReparar: "Reparar",
    mode: "Modo",
    modeStandard: "Standard",
    modeExpress: "Express",
    modeInmediato: "Inmediato",
    perRacketTitle: "Por raqueta",
    racketLabel: "Raqueta",
    priceTotal: "Precio total estimado",
    pickDay: "Día disponible",
    pickSlot: "Hora disponible",
    observations: "Observaciones",
    obsPlaceholder: "Notas para el técnico (opcional)",
    cta: "Solicitar reserva (preview)",
    noBackend: "Preview · no se envía nada real.",
    needEqHint: "Añade primero al menos una raqueta en “Mi equipación”.",

    // ---- Tickets
    statusTitle: "Estado de tus raquetas",
    statusIntro: "Reservas, planificaciones y entradas sin cita.",
    statusFields: {
      ref: "Referencia", model: "Modelo", peso: "Peso", grip: "Grip", year: "Año",
      cordaje: "Cordaje", tension: "Tensión", estado: "Estado",
      entrega: "Entrega estimada", qr_rfid: "QR / RFID", source: "Origen",
      store: "Tienda",
    },

    legalBlurb: "Modo Preview · sin reserva pública · sin pagos · sin notificaciones reales.",
  },
  en: {
    sectionTitle: "Racket Lab — player area",
    sectionBadge: "Controlled preview",
    sectionNote: "Player-side preview · demo data, no real booking.",

    tabEquipment: "My gear",
    tabMyRackets: "My rackets",
    tabReservation: "Stringing booking",
    tabQr: "My QR",

    eqTitle: "My gear · Rackets",
    eqIntro: "Define your kit. Each racket has an internal reference (renameable), an associated QR or RFID code and a model (from catalog or typed manually).",
    eqAdd: "Add racket",
    eqEmpty: "You have no rackets yet. Tap “Add racket”.",
    eqRacketHeader: "Racket",
    eqInternalRef: "Internal reference",
    eqInternalRefHelp: "What you call it (e.g. “My Blade #1”).",
    eqQr: "Associated QR",
    eqRfid: "Associated RFID",
    eqOptional: "optional",
    eqModel: "Model",
    eqModelManual: "Type manually",
    eqModelFromList: "Pick from catalog",
    // MI-RAQUETAS-SELECTOR-OMIT-L1-1 (Feb 2026)
    eqImplicitProductTitle: "Product = Racket (implicit in this tab)",
    eqImplicitProductHelp: "Pick Brand → Model → Grip → Weight. category_key=\"raqueta\" is kept internally.",
    eqBrand: "Brand",
    eqModelName: "Model",
    eqWeight: "Weight (g)",
    eqGrip: "Grip",
    eqYear: "Year",
    eqRename: "Rename",
    eqDone: "Done",
    eqRemove: "Remove",
    eqConfirmRemove: "Remove this racket from your gear?",
    eqCountLabel: "Number of rackets",
    eqLocalNote: "Changes are stored in this browser only (preview).",

    myQrTitle: "My QR",
    myQrBody: "Quick identification for your next visit. Your QR exposes no personal data.",
    myQrCta: "Show my QR",
    myQrModalClose: "Close",
    myQrModalDesc: "Preview view. In controlled production this binds to your TEAM passport with no PII exposure.",
    myQrScanHint: "The stringer scans this code to open your file on tablet.",

    storeTitle: "Stringing booking",
    storeBody: "Pick a store to bring your rackets.",
    storeSelectLabel: "Store",
    storeSelectPlaceholder: "— Choose a store —",
    storeAddress: "Address",
    storePhone: "Phone",
    storeEmail: "Email",
    storeEta: "Average lead time",
    storeOpenMaps: "Open in Maps",

    racketCount: "Number of rackets",
    pickRackets: "Pick rackets",
    pickRacketsHelp: "Choose which ones you’re bringing. Sourced from “My gear”.",
    services: "Services",
    servicesEncordar: "String",
    servicesRevisar: "Review",
    servicesReparar: "Repair",
    mode: "Mode",
    modeStandard: "Standard",
    modeExpress: "Express",
    modeInmediato: "Immediate",
    perRacketTitle: "Per racket",
    racketLabel: "Racket",
    priceTotal: "Estimated total price",
    pickDay: "Available day",
    pickSlot: "Available slot",
    observations: "Notes",
    obsPlaceholder: "Notes for the technician (optional)",
    cta: "Request booking (preview)",
    noBackend: "Preview · nothing real is sent.",
    needEqHint: "Add at least one racket in “My gear” first.",

    statusTitle: "Your rackets status",
    statusIntro: "Reservations, planned drop-offs and walk-ins.",
    statusFields: {
      ref: "Reference", model: "Model", peso: "Weight", grip: "Grip", year: "Year",
      cordaje: "Strings", tension: "Tension", estado: "Status",
      entrega: "Estimated delivery", qr_rfid: "QR / RFID", source: "Source",
      store: "Store",
    },

    legalBlurb: "Preview mode · no public booking · no payments · no real notifications.",
  },
};

// -----------------------------------------------------------------------------
// Helpers
// -----------------------------------------------------------------------------
const LS_KEY = "tsw_player_rackets_v1";
const mapsUrl = (address) => `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(address)}`;
const newRacketId = () => `r-${Math.random().toString(36).slice(2, 8)}-${Date.now().toString(36).slice(-4)}`;
const newQrCode = () => `QR-${Math.random().toString(36).slice(2, 6).toUpperCase()}-${Math.random().toString(36).slice(2, 6).toUpperCase()}`;

export const loadRackets = () => {
  try {
    const raw = localStorage.getItem(LS_KEY);
    if (!raw) return DEFAULT_RACKETS;
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return DEFAULT_RACKETS;
    return parsed;
  } catch { return DEFAULT_RACKETS; }
};
export const saveRackets = (list) => {
  try { localStorage.setItem(LS_KEY, JSON.stringify(list)); } catch { /* ignore quota */ }
};

// Realistic-looking mock QR (24×24 deterministic pattern with the three
// finder squares · NOT a real scannable code, but renders cleanly at any
// size and reads as a believable QR for VIP demo purposes).
export function MockQrSvg({ size = 32, seed = "TEAM-PLAYER", showCorners = true, dataTestId = "player-myqr-svg" }) {
  const N = 25;
  // Deterministic hash from seed string → fills cells
  let h = 2166136261;
  for (let i = 0; i < seed.length; i++) {
    h ^= seed.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  const isFinder = (x, y, ox, oy) => x >= ox && x < ox + 7 && y >= oy && y < oy + 7;
  const cells = [];
  for (let y = 0; y < N; y++) {
    const row = [];
    for (let x = 0; x < N; x++) {
      const inTL = isFinder(x, y, 0, 0);
      const inTR = isFinder(x, y, N - 7, 0);
      const inBL = isFinder(x, y, 0, N - 7);
      if (showCorners && (inTL || inTR || inBL)) {
        // Finder pattern: solid 7x7 outline + 3x3 center
        let ox = 0, oy = 0;
        if (inTR) ox = N - 7;
        if (inBL) oy = N - 7;
        const lx = x - ox, ly = y - oy;
        const onBorder = lx === 0 || lx === 6 || ly === 0 || ly === 6;
        const onInner = lx >= 2 && lx <= 4 && ly >= 2 && ly <= 4;
        row.push(onBorder || onInner ? 1 : 0);
      } else {
        h ^= (x * 73856093) ^ (y * 19349663);
        h = Math.imul(h, 16777619);
        row.push((h >>> 24) & 1);
      }
    }
    cells.push(row);
  }
  return (
    <svg
      viewBox={`0 0 ${N} ${N}`}
      style={{ width: size, height: size, imageRendering: "pixelated", background: "white" }}
      aria-label="QR preview"
      data-testid={dataTestId}
    >
      <rect width={N} height={N} fill="#fff" />
      {cells.map((row, y) => row.map((c, x) => c ? (
        <rect key={`${x}-${y}`} x={x} y={y} width="1" height="1" fill="#0f172a" />
      ) : null))}
    </svg>
  );
}

export function QrModal({ open, onClose, tx, seed }) {
  if (!open) return null;
  return (
    <div
      className="fixed inset-0 bg-slate-900/70 z-50 flex items-center justify-center p-4"
      data-testid="player-myqr-modal"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-2xl shadow-xl max-w-md w-full p-6"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between mb-3">
          <h3 className="text-xl font-semibold text-slate-900" data-testid="player-myqr-modal-title">
            {tx.myQrTitle}
          </h3>
          <button onClick={onClose} className="text-slate-500 hover:text-slate-700" data-testid="player-myqr-modal-close" aria-label={tx.myQrModalClose}>
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className="flex flex-col items-center gap-3">
          <div className="p-4 rounded-xl bg-white border border-slate-200 shadow-sm">
            <MockQrSvg size={280} seed={seed} dataTestId="player-myqr-svg-large" />
          </div>
          <p className="text-sm text-slate-700 text-center">{tx.myQrScanHint}</p>
          <p className="text-xs text-slate-500 text-center max-w-sm">{tx.myQrModalDesc}</p>
        </div>
      </div>
    </div>
  );
}

// =============================================================================
// Sub-component: Mi equipación editor
// =============================================================================
export function EquipmentEditor({ tx, rackets, setRackets }) {
  const [editingId, setEditingId] = useState(null);

  // MI-RAQUETAS-SELECTOR-OMIT-L1-1 (Feb 2026) — derive catalog_ref from
  // the racket cascade state. category_key="raqueta" is implicit by
  // context (we are inside Mis raquetas). Slug helpers are minimal and
  // local; the canonical CatalogRef schema lives in
  // app.modules.catalog_intelligence.models (F1.b, backend Pydantic).
  const _slug = (s) => String(s || "").toLowerCase().trim().replace(/\s+/g, "-").replace(/[^a-z0-9-]/g, "");
  const buildCatalogRef = (r) => {
    if (!r || r.manual) return null;
    if (!r.brand || !r.model || !r.grip || !r.weight_g) return null;
    return {
      category_key: "raqueta",
      brand_key: _slug(r.brand),
      model_key: _slug(r.model),
      variant_key: `${_slug(r.model)}-${String(r.grip).toLowerCase()}-${r.weight_g}g`,
      freetext_fallback: null,
    };
  };

  const addRacket = () => {
    const id = newRacketId();
    setRackets([
      ...rackets,
      {
        id,
        internal_ref: `${tx.racketLabel} #${rackets.length + 1}`,
        qr_short: newQrCode(),
        rfid: "",
        brand: "",
        model: "",
        weight_g: "",
        grip: "",
        year: "",
        manual: true,
      },
    ]);
    setEditingId(id);
  };
  const removeRacket = (id) => {
    if (!window.confirm(tx.eqConfirmRemove)) return;
    setRackets(rackets.filter((r) => r.id !== id));
  };
  const updateRacket = (id, patch) => {
    setRackets(rackets.map((r) => r.id === id ? { ...r, ...patch } : r));
  };
  const applyCatalog = (id, idx) => {
    const item = RACKET_CATALOG[idx];
    if (!item) return;
    updateRacket(id, {
      brand: item.brand,
      model: item.model,
      weight_g: item.weight_g,
      grip: item.grips[0] || "",
      year: item.year,
      manual: false,
    });
  };

  return (
    <div data-testid="player-eq-editor" className="space-y-4">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h3 className="text-lg font-semibold text-slate-900" data-testid="player-eq-title">{tx.eqTitle}</h3>
          <p className="text-sm text-slate-600 max-w-xl mt-1">{tx.eqIntro}</p>
        </div>
        <div className="flex items-center gap-3 text-sm">
          <div className="text-xs text-slate-500">
            <span className="font-medium text-slate-700">{tx.eqCountLabel}:</span> <span data-testid="player-eq-count">{rackets.length}</span>
          </div>
          <button
            type="button"
            onClick={addRacket}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-semibold"
            data-testid="player-eq-add"
          >
            <Plus className="w-3.5 h-3.5" /> {tx.eqAdd}
          </button>
        </div>
      </div>

      {/* MI-RAQUETAS-SELECTOR-OMIT-L1-1 (Feb 2026) — context banner.
          Producto = Raqueta queda implícito por contexto en esta vista;
          la UI omite L1 pero internamente catalog_ref.category_key="raqueta". */}
      <div
        className="rounded-lg border border-emerald-200 bg-emerald-50/60 px-3 py-2 text-xs text-emerald-900 flex items-start gap-2"
        data-testid="player-eq-implicit-product-banner"
      >
        <span className="font-semibold">ℹ</span>
        <span>
          <span className="font-semibold">{tx.eqImplicitProductTitle}</span>
          {" · "}
          <span>{tx.eqImplicitProductHelp}</span>
        </span>
      </div>

      {rackets.length === 0 ? (
        <div className="rounded-xl border border-dashed border-slate-300 bg-white px-4 py-8 text-center text-sm text-slate-500" data-testid="player-eq-empty">
          {tx.eqEmpty}
        </div>
      ) : (
        <ul className="space-y-3" data-testid="player-eq-list">
          {rackets.map((r, idx) => {
            const isEditing = editingId === r.id;
            return (
              <li
                key={r.id}
                className="rounded-xl border border-slate-200 bg-white p-4"
                data-testid={`player-eq-item-${idx + 1}`}
              >
                <div className="flex items-start justify-between gap-3 mb-3">
                  <div className="flex-1 min-w-0">
                    <div className="text-[11px] uppercase tracking-wider text-slate-500">
                      {tx.eqRacketHeader} {idx + 1}
                    </div>
                    {isEditing ? (
                      <input
                        type="text"
                        value={r.internal_ref}
                        onChange={(e) => updateRacket(r.id, { internal_ref: e.target.value })}
                        className="mt-1 w-full rounded-lg border border-emerald-300 bg-emerald-50 px-2 py-1 text-sm font-semibold text-slate-900"
                        data-testid={`player-eq-item-${idx + 1}-ref-input`}
                      />
                    ) : (
                      <div
                        className="text-base font-semibold text-slate-900 truncate"
                        data-testid={`player-eq-item-${idx + 1}-ref`}
                      >
                        {r.internal_ref || "—"}
                      </div>
                    )}
                    <div className="text-[11px] text-slate-500 mt-0.5">{tx.eqInternalRefHelp}</div>
                  </div>
                  <div className="flex items-center gap-1.5 shrink-0">
                    {isEditing ? (
                      <button
                        type="button"
                        onClick={() => setEditingId(null)}
                        className="px-2 py-1 rounded-md bg-emerald-600 hover:bg-emerald-700 text-white text-[11px] inline-flex items-center gap-1"
                        data-testid={`player-eq-item-${idx + 1}-done`}
                      >
                        <Check className="w-3 h-3" /> {tx.eqDone}
                      </button>
                    ) : (
                      <button
                        type="button"
                        onClick={() => setEditingId(r.id)}
                        className="px-2 py-1 rounded-md border border-slate-300 bg-white hover:bg-slate-50 text-slate-700 text-[11px] inline-flex items-center gap-1"
                        data-testid={`player-eq-item-${idx + 1}-rename`}
                      >
                        <Pencil className="w-3 h-3" /> {tx.eqRename}
                      </button>
                    )}
                    <button
                      type="button"
                      onClick={() => removeRacket(r.id)}
                      className="px-2 py-1 rounded-md border border-rose-200 bg-white hover:bg-rose-50 text-rose-600 text-[11px] inline-flex items-center gap-1"
                      data-testid={`player-eq-item-${idx + 1}-remove`}
                    >
                      <Trash2 className="w-3 h-3" /> {tx.eqRemove}
                    </button>
                  </div>
                </div>

                <div className="grid sm:grid-cols-2 gap-3">
                  <div>
                    <label className="block text-[11px] font-medium text-slate-600 mb-1">
                      {tx.eqQr}
                    </label>
                    <input
                      type="text"
                      value={r.qr_short}
                      onChange={(e) => updateRacket(r.id, { qr_short: e.target.value })}
                      className="w-full rounded-lg border border-slate-300 bg-white px-2 py-1.5 text-sm font-mono"
                      data-testid={`player-eq-item-${idx + 1}-qr`}
                    />
                  </div>
                  <div>
                    <label className="block text-[11px] font-medium text-slate-600 mb-1">
                      {tx.eqRfid} <span className="text-slate-400 normal-case">({tx.eqOptional})</span>
                    </label>
                    <input
                      type="text"
                      value={r.rfid || ""}
                      onChange={(e) => updateRacket(r.id, { rfid: e.target.value })}
                      className="w-full rounded-lg border border-slate-300 bg-white px-2 py-1.5 text-sm font-mono"
                      data-testid={`player-eq-item-${idx + 1}-rfid`}
                    />
                  </div>
                </div>

                <div className="mt-4 rounded-lg border border-slate-200 bg-slate-50 p-3 space-y-2">
                  <div className="flex items-center justify-between gap-2 flex-wrap">
                    <div className="text-xs font-semibold text-slate-700 inline-flex items-center gap-1.5">
                      <Package className="w-3.5 h-3.5 text-emerald-700" /> {tx.eqModel}
                    </div>
                    <div className="flex gap-1 text-[11px]">
                      <button
                        type="button"
                        onClick={() => updateRacket(r.id, { manual: false })}
                        className={`px-2 py-0.5 rounded-md border ${!r.manual ? "bg-emerald-600 border-emerald-600 text-white" : "bg-white border-slate-300 text-slate-700"}`}
                        data-testid={`player-eq-item-${idx + 1}-mode-list`}
                      >
                        {tx.eqModelFromList}
                      </button>
                      <button
                        type="button"
                        onClick={() => updateRacket(r.id, { manual: true })}
                        className={`px-2 py-0.5 rounded-md border ${r.manual ? "bg-emerald-600 border-emerald-600 text-white" : "bg-white border-slate-300 text-slate-700"}`}
                        data-testid={`player-eq-item-${idx + 1}-mode-manual`}
                      >
                        {tx.eqModelManual}
                      </button>
                    </div>
                  </div>

                  {!r.manual ? (
                    // MI-RAQUETAS-SELECTOR-OMIT-L1-1 (Feb 2026) — cascada
                    // Marca → Modelo → Grip → Peso. category_key="raqueta"
                    // queda implícito por contexto (estás en Mis raquetas).
                    // El catalog_ref se reconstruye desde el state actual.
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-2" data-testid={`player-eq-item-${idx + 1}-cascade`}>
                      {(() => {
                        const brands = Array.from(new Set(RACKET_CATALOG.map((it) => it.brand))).sort();
                        const modelsForBrand = r.brand
                          ? Array.from(new Set(RACKET_CATALOG.filter((it) => it.brand === r.brand).map((it) => it.model))).sort()
                          : [];
                        const matchModel = RACKET_CATALOG.find((it) => it.brand === r.brand && it.model === r.model);
                        const gripsForModel = matchModel ? matchModel.grips : [];
                        const weightsForCombo = matchModel ? [matchModel.weight_g] : [];
                        return (
                          <>
                            <div>
                              <label className="block text-[10px] uppercase tracking-wider text-emerald-700 mb-0.5">1 · {tx.eqBrand}</label>
                              <select
                                value={r.brand || ""}
                                onChange={(e) => updateRacket(r.id, {
                                  brand: e.target.value,
                                  model: "", weight_g: "", grip: "",
                                  year: "",
                                  manual: false,
                                })}
                                className="w-full rounded-lg border border-slate-300 bg-white px-2 py-1.5 text-sm"
                                data-testid={`player-eq-item-${idx + 1}-cascade-brand`}
                              >
                                <option value="">—</option>
                                {brands.map((b) => <option key={b} value={b}>{b}</option>)}
                              </select>
                            </div>
                            <div>
                              <label className="block text-[10px] uppercase tracking-wider text-emerald-700 mb-0.5">2 · {tx.eqModelName}</label>
                              <select
                                value={r.model || ""}
                                onChange={(e) => {
                                  const m = RACKET_CATALOG.find((it) => it.brand === r.brand && it.model === e.target.value);
                                  updateRacket(r.id, {
                                    model: e.target.value,
                                    grip: "",
                                    weight_g: m ? m.weight_g : "",
                                    year: m ? m.year : "",
                                  });
                                }}
                                disabled={!r.brand}
                                className="w-full rounded-lg border border-slate-300 bg-white px-2 py-1.5 text-sm disabled:bg-slate-50 disabled:text-slate-400"
                                data-testid={`player-eq-item-${idx + 1}-cascade-model`}
                              >
                                <option value="">—</option>
                                {modelsForBrand.map((m) => <option key={m} value={m}>{m}</option>)}
                              </select>
                            </div>
                            <div>
                              <label className="block text-[10px] uppercase tracking-wider text-emerald-700 mb-0.5">3 · {tx.eqGrip}</label>
                              <select
                                value={r.grip || ""}
                                onChange={(e) => updateRacket(r.id, { grip: e.target.value })}
                                disabled={!r.model}
                                className="w-full rounded-lg border border-slate-300 bg-white px-2 py-1.5 text-sm disabled:bg-slate-50 disabled:text-slate-400"
                                data-testid={`player-eq-item-${idx + 1}-cascade-grip`}
                              >
                                <option value="">—</option>
                                {gripsForModel.map((g) => <option key={g} value={g}>{g}</option>)}
                              </select>
                            </div>
                            <div>
                              <label className="block text-[10px] uppercase tracking-wider text-emerald-700 mb-0.5">4 · {tx.eqWeight}</label>
                              <select
                                value={r.weight_g === "" ? "" : String(r.weight_g)}
                                onChange={(e) => updateRacket(r.id, { weight_g: e.target.value ? Number(e.target.value) : "" })}
                                disabled={!r.grip}
                                className="w-full rounded-lg border border-slate-300 bg-white px-2 py-1.5 text-sm disabled:bg-slate-50 disabled:text-slate-400"
                                data-testid={`player-eq-item-${idx + 1}-cascade-weight`}
                              >
                                <option value="">—</option>
                                {weightsForCombo.map((w) => <option key={w} value={String(w)}>{w}g</option>)}
                              </select>
                            </div>
                          </>
                        );
                      })()}
                    </div>
                  ) : null}

                  <div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
                    <div>
                      <label className="block text-[10px] text-slate-500 mb-0.5">{tx.eqBrand}</label>
                      <input
                        type="text"
                        value={r.brand}
                        onChange={(e) => updateRacket(r.id, { brand: e.target.value })}
                        readOnly={!r.manual}
                        className={`w-full rounded-md border px-2 py-1 text-xs ${r.manual ? "border-slate-300 bg-white" : "border-slate-200 bg-white/60 text-slate-700"}`}
                        data-testid={`player-eq-item-${idx + 1}-brand`}
                      />
                    </div>
                    <div className="col-span-2 sm:col-span-2">
                      <label className="block text-[10px] text-slate-500 mb-0.5">{tx.eqModelName}</label>
                      <input
                        type="text"
                        value={r.model}
                        onChange={(e) => updateRacket(r.id, { model: e.target.value })}
                        readOnly={!r.manual}
                        className={`w-full rounded-md border px-2 py-1 text-xs ${r.manual ? "border-slate-300 bg-white" : "border-slate-200 bg-white/60 text-slate-700"}`}
                        data-testid={`player-eq-item-${idx + 1}-model`}
                      />
                    </div>
                    <div>
                      <label className="block text-[10px] text-slate-500 mb-0.5">{tx.eqWeight}</label>
                      <input
                        type="text"
                        value={r.weight_g}
                        onChange={(e) => updateRacket(r.id, { weight_g: e.target.value })}
                        readOnly={!r.manual}
                        className={`w-full rounded-md border px-2 py-1 text-xs ${r.manual ? "border-slate-300 bg-white" : "border-slate-200 bg-white/60 text-slate-700"}`}
                        data-testid={`player-eq-item-${idx + 1}-weight`}
                      />
                    </div>
                    <div>
                      <label className="block text-[10px] text-slate-500 mb-0.5">{tx.eqGrip}</label>
                      <input
                        type="text"
                        value={r.grip}
                        onChange={(e) => updateRacket(r.id, { grip: e.target.value })}
                        className="w-full rounded-md border border-slate-300 bg-white px-2 py-1 text-xs"
                        data-testid={`player-eq-item-${idx + 1}-grip`}
                      />
                    </div>
                    <div>
                      <label className="block text-[10px] text-slate-500 mb-0.5">{tx.eqYear}</label>
                      <input
                        type="text"
                        value={r.year}
                        onChange={(e) => updateRacket(r.id, { year: e.target.value })}
                        readOnly={!r.manual}
                        className={`w-full rounded-md border px-2 py-1 text-xs ${r.manual ? "border-slate-300 bg-white" : "border-slate-200 bg-white/60 text-slate-700"}`}
                        data-testid={`player-eq-item-${idx + 1}-year`}
                      />
                    </div>
                  </div>
                  {/* MI-RAQUETAS-SELECTOR-OMIT-L1-1 — catalog_ref chip
                      visible solo cuando la cascada está completa. */}
                  {(() => {
                    const cref = buildCatalogRef(r);
                    if (!cref) return null;
                    return (
                      <div
                        className="mt-2 inline-flex items-center gap-2 rounded-full bg-emerald-100 text-emerald-800 text-[10px] font-semibold px-2.5 py-1"
                        data-testid={`player-eq-item-${idx + 1}-catalog-ref`}
                      >
                        <span>✅ catalog_ref</span>
                        <span className="font-mono text-[10px] text-emerald-900">
                          {cref.category_key}/{cref.brand_key}/{cref.model_key}/{cref.variant_key}
                        </span>
                      </div>
                    );
                  })()}
                </div>
              </li>
            );
          })}
        </ul>
      )}

      <p className="text-[11px] text-slate-500 italic" data-testid="player-eq-local-note">{tx.eqLocalNote}</p>
    </div>
  );
}

// =============================================================================
// Sub-component: Reservation
// =============================================================================
export function ReservationBlock({ tx, rackets }) {
  const [storeId, setStoreId] = useState("");
  const [pickedIds, setPickedIds] = useState(() => rackets.slice(0, 1).map((r) => r.id));
  const [servicesByRacket, setServicesByRacket] = useState({});
  const [mode, setMode] = useState("standard");
  const [day, setDay] = useState("");
  const [slot, setSlot] = useState("");
  const [observations, setObservations] = useState("");

  // Derive the effective picked list every render: if a previously
  // picked racket has been removed from "Mi equipación", drop it from
  // the displayed picks without mutating state inside an effect.
  const effectivePickedIds = useMemo(
    () => pickedIds.filter((id) => rackets.some((r) => r.id === id)),
    [pickedIds, rackets],
  );

  const store = storeId ? MOCK_STORES[storeId] : null;
  const togglePick = (id) => {
    setPickedIds((prev) => prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]);
  };
  const toggleService = (rid, svc) => {
    setServicesByRacket((prev) => ({
      ...prev,
      [rid]: { ...(prev[rid] || { encordar: true }), [svc]: !((prev[rid] || { encordar: true })[svc]) },
    }));
  };

  const total = useMemo(() => {
    const sum = effectivePickedIds.reduce((acc, rid) => {
      const s = servicesByRacket[rid] || { encordar: true };
      return acc + (s.encordar ? PRICE.encordar : 0) + (s.revisar ? PRICE.revisar : 0) + (s.reparar ? PRICE.reparar : 0);
    }, 0);
    return Math.round(sum * (PRICE.mode_multiplier[mode] || 1));
  }, [effectivePickedIds, servicesByRacket, mode]);

  if (rackets.length === 0) {
    return (
      <div className="rounded-xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-800" data-testid="player-reservation-need-eq">
        {tx.needEqHint}
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="player-reservation-block">
      <div>
        <h3 className="text-lg font-semibold text-slate-900 flex items-center gap-2" data-testid="player-store-title">
          <Store className="w-4 h-4 text-emerald-700" /> {tx.storeTitle}
        </h3>
        <p className="text-sm text-slate-600 mt-1">{tx.storeBody}</p>
      </div>

      <div>
        <label className="block text-xs font-medium text-slate-700 mb-1" htmlFor="rl-store-select">{tx.storeSelectLabel}</label>
        <select
          id="rl-store-select"
          value={storeId}
          onChange={(e) => setStoreId(e.target.value)}
          className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm"
          data-testid="player-store-select"
        >
          <option value="">{tx.storeSelectPlaceholder}</option>
          {Object.entries(MOCK_STORES).map(([id, s]) => (
            <option key={id} value={id}>{s.name} · {s.eta}</option>
          ))}
        </select>
      </div>

      {store && (
        <>
          <div className="rounded-lg bg-emerald-50 border border-emerald-200 p-3 text-xs space-y-1.5" data-testid="player-store-details">
            <div className="font-semibold text-slate-900 text-sm">{store.name}</div>
            <div className="flex items-start gap-1.5">
              <MapPin className="w-3.5 h-3.5 text-emerald-700 mt-0.5 shrink-0" />
              <div>
                <span data-testid="player-store-address">{store.address}</span>
                {" · "}
                <a
                  href={mapsUrl(store.address)}
                  target="_blank"
                  rel="noreferrer"
                  className="text-emerald-700 underline font-medium"
                  data-testid="player-store-maps-link"
                >
                  {tx.storeOpenMaps}
                </a>
              </div>
            </div>
            <div className="flex items-center gap-1.5">
              <Phone className="w-3.5 h-3.5 text-emerald-700 shrink-0" />
              <a href={`tel:${store.phone.replace(/\s+/g, "")}`} className="text-slate-700" data-testid="player-store-phone">{store.phone}</a>
            </div>
            <div className="flex items-center gap-1.5">
              <Mail className="w-3.5 h-3.5 text-emerald-700 shrink-0" />
              <a href={`mailto:${store.email}`} className="text-slate-700" data-testid="player-store-email">{store.email}</a>
            </div>
            <div><span className="font-semibold text-slate-700">{tx.storeEta}:</span> {store.eta}</div>
          </div>

          <div>
            <div className="flex items-center justify-between mb-1">
              <label className="block text-xs font-medium text-slate-700">{tx.pickRackets}</label>
              <span className="text-[11px] text-slate-500">{tx.pickRacketsHelp}</span>
            </div>
            <div className="grid sm:grid-cols-2 gap-2" data-testid="player-pick-rackets-grid">
              {rackets.map((r, idx) => {
                const picked = effectivePickedIds.includes(r.id);
                const s = servicesByRacket[r.id] || { encordar: true, revisar: false, reparar: false };
                return (
                  <div
                    key={r.id}
                    className={`rounded-lg border p-2.5 text-xs ${picked ? "border-emerald-400 bg-emerald-50" : "border-slate-200 bg-slate-50"}`}
                    data-testid={`player-pick-racket-${idx + 1}`}
                  >
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={picked}
                        onChange={() => togglePick(r.id)}
                        data-testid={`player-pick-racket-${idx + 1}-check`}
                      />
                      <div className="flex-1 min-w-0">
                        <div className="font-semibold text-slate-900 truncate">{r.internal_ref}</div>
                        <div className="text-[10px] text-slate-500 truncate">
                          {r.brand} {r.model}{r.weight_g ? ` · ${r.weight_g}g` : ""}{r.grip ? ` · ${r.grip}` : ""}
                        </div>
                      </div>
                    </label>
                    {picked && (
                      <div className="mt-2 flex flex-wrap gap-2 border-t border-emerald-200 pt-1.5">
                        {SERVICES.map((svc) => (
                          <label key={svc} className="inline-flex items-center gap-1 text-[11px]">
                            <input
                              type="checkbox"
                              checked={!!s[svc]}
                              onChange={() => toggleService(r.id, svc)}
                              data-testid={`player-svc-${svc}-${idx + 1}`}
                            />
                            <span>{tx[`services${svc.charAt(0).toUpperCase()+svc.slice(1)}`]}</span>
                          </label>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          <div>
            <label className="block text-xs font-medium text-slate-700 mb-1">{tx.mode}</label>
            <div className="flex gap-1" data-testid="player-mode-group">
              {MODES.map((m) => (
                <button
                  key={m}
                  type="button"
                  onClick={() => setMode(m)}
                  className={`flex-1 px-2 py-1.5 rounded-lg border text-xs font-semibold ${mode === m ? "bg-emerald-600 text-white border-emerald-600" : "bg-white text-slate-700 border-slate-300"}`}
                  data-testid={`player-mode-${m}`}
                >
                  {tx[`mode${m.charAt(0).toUpperCase()+m.slice(1)}`]}
                </button>
              ))}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-slate-700 mb-1 flex items-center gap-1"><CalIcon className="w-3 h-3" /> {tx.pickDay}</label>
              <select
                value={day}
                onChange={(e) => { setDay(e.target.value); setSlot(""); }}
                className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm"
                data-testid="player-pick-day"
              >
                <option value="">—</option>
                {store.available_days.map((d) => <option key={d} value={d}>{d}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-700 mb-1">{tx.pickSlot}</label>
              <select
                value={slot}
                onChange={(e) => setSlot(e.target.value)}
                disabled={!day}
                className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm disabled:opacity-50"
                data-testid="player-pick-slot"
              >
                <option value="">—</option>
                {day && store.available_slots.map((sl) => <option key={sl} value={sl}>{sl}</option>)}
              </select>
            </div>
          </div>

          <div>
            <label className="block text-xs font-medium text-slate-700 mb-1" htmlFor="rl-obs">{tx.observations}</label>
            <textarea
              id="rl-obs"
              rows={2}
              value={observations}
              onChange={(e) => setObservations(e.target.value)}
              placeholder={tx.obsPlaceholder}
              className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm"
              data-testid="player-observations"
            />
          </div>

          <div className="flex items-center justify-between border-t border-slate-200 pt-3">
            <div className="text-xs text-slate-500">{tx.noBackend}</div>
            <div className="text-right">
              <div className="text-xs text-slate-500">{tx.priceTotal}</div>
              <div className="text-2xl font-bold text-emerald-700" data-testid="player-price-total">{total} €</div>
            </div>
          </div>

          <button
            type="button"
            disabled
            className="w-full px-4 py-2 rounded-lg bg-emerald-600 text-white text-sm font-semibold opacity-90 cursor-default"
            data-testid="player-reservation-cta"
            title="preview"
          >
            {tx.cta}
          </button>
        </>
      )}
    </div>
  );
}

// =============================================================================
// Sub-component: Tickets list (also exported for the standalone Mis raquetas page)
// =============================================================================
export function TicketsBlock({ tx, rackets, lang }) {
  const stateLabels = STATE_LABELS[lang] || STATE_LABELS.es;
  const srcLabels = SOURCE_LABELS[lang] || SOURCE_LABELS.es;
  const racketIndex = useMemo(() => {
    const map = {};
    rackets.forEach((r) => { map[r.id] = r; });
    return map;
  }, [rackets]);

  return (
    <div className="space-y-3" data-testid="player-tickets-block">
      <div>
        <h3 className="text-lg font-semibold text-slate-900 flex items-center gap-2"><ListChecks className="w-4 h-4 text-emerald-700" />{tx.statusTitle}</h3>
        <p className="text-xs text-slate-500 mt-1">{tx.statusIntro}</p>
      </div>
      <ul className="space-y-2">
        {MOCK_TICKETS.map((t, i) => {
          // Hydrate ticket from racket index when source = reservation.
          const r = racketIndex[t.racket_id] || {};
          const internal_ref = t.internal_ref || r.internal_ref || "—";
          const qr_short = t.qr_short || r.qr_short || "—";
          const rfid = t.rfid != null ? t.rfid : r.rfid;
          const brand = t.brand || r.brand || "—";
          const model = t.model || r.model || "—";
          const weight_g = t.weight_g || r.weight_g || "—";
          const grip = t.grip || r.grip || "—";
          const year = t.year || r.year || "—";
          return (
            <li
              key={i}
              className="rounded-lg border border-slate-200 bg-white p-3 text-xs space-y-1"
              data-testid={`player-ticket-${i + 1}`}
            >
              <div className="flex items-center justify-between flex-wrap gap-1">
                <div className="font-semibold text-slate-900" data-testid={`player-ticket-${i + 1}-ref`}>{internal_ref}</div>
                <div className="flex items-center gap-1.5">
                  <span
                    className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-slate-100 text-[10px] font-semibold text-slate-700 border border-slate-200"
                    data-testid={`player-ticket-${i + 1}-source`}
                  >
                    {srcLabels[t.source]}
                  </span>
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-white text-[10px] font-semibold text-slate-700 border border-slate-200">
                    <span className={`w-2 h-2 rounded-full ${STATE_DOT[t.state_key]}`} />
                    <span data-testid={`player-ticket-${i + 1}-state`}>{stateLabels[t.state_key]}</span>
                  </span>
                </div>
              </div>
              <div className="text-[11px] text-slate-500" data-testid={`player-ticket-${i + 1}-qr`}>
                {tx.statusFields.qr_rfid}: <span className="font-mono">{qr_short}</span>{rfid ? <> · <span className="font-mono">{rfid}</span></> : null}
              </div>
              <div data-testid={`player-ticket-${i + 1}-model`}>
                <span className="text-slate-500">{tx.statusFields.model}:</span> {brand} {model} · {weight_g}g · {tx.statusFields.grip} {grip} · {year}
              </div>
              <div><span className="text-slate-500">{tx.statusFields.cordaje}:</span> {t.strings}</div>
              <div><span className="text-slate-500">{tx.statusFields.tension}:</span> {t.tension_kg} kg</div>
              <div><span className="text-slate-500">{tx.statusFields.store}:</span> {t.store}</div>
              <div><span className="text-slate-500">{tx.statusFields.entrega}:</span> {t.eta}</div>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

// =============================================================================
// Main component
// =============================================================================
export default function RacketLabPlayerPreview() {
  const { lang } = useLang();
  const tx = T[lang] || T.es;

  const [rackets, setRackets] = useState(loadRackets);
  useEffect(() => { saveRackets(rackets); }, [rackets]);

  // Default to "Mi equipación" tab so the player first sees their gear.
  const [tab, setTab] = useState("equipment");
  const [qrOpen, setQrOpen] = useState(false);

  // If hash matches a tab anchor, jump straight to that tab and scroll.
  // Note: `Mis raquetas` is now a standalone page (/app/my-rackets) since
  // DASHBOARD-SIDEMENU-PROFILES-1-FIX1, so we no longer handle that hash.
  useEffect(() => {
    const apply = () => {
      const h = (window.location.hash || "").toLowerCase();
      let targetId = null;
      if (h.includes("mi-qr"))         { setTab("qr");          targetId = "player-mi-qr"; }
      else if (h.includes("reserva"))  { setTab("reservation"); targetId = "player-racket-lab"; }
      else if (h.includes("equipa"))   { setTab("equipment");   targetId = "player-racket-lab"; }
      else if (h.includes("racket-lab")) { targetId = "player-racket-lab"; }
      if (targetId) {
        window.requestAnimationFrame(() => {
          const el = document.getElementById(targetId);
          if (el) el.scrollIntoView({ block: "start", behavior: "smooth" });
        });
      }
    };
    apply();
    window.addEventListener("hashchange", apply);
    return () => window.removeEventListener("hashchange", apply);
  }, []);

  const TABS = [
    { key: "equipment",   label: tx.tabEquipment,   icon: Package,    testid: "player-tab-equipment" },
    { key: "qr",          label: tx.tabQr,          icon: QrCode,     testid: "player-tab-qr" },
    { key: "reservation", label: tx.tabReservation, icon: Store,      testid: "player-tab-reservation" },
  ];

  return (
    <section
      id="player-racket-lab"
      data-testid="player-racket-lab-preview"
      className="mt-8 rounded-2xl border border-emerald-200 bg-emerald-50/40 p-4 sm:p-6 scroll-mt-24"
    >
      <header className="flex flex-wrap items-center gap-3 mb-1">
        <Wrench className="w-5 h-5 text-emerald-700" />
        <h2 className="text-xl sm:text-2xl font-semibold text-slate-900" data-testid="player-rl-section-title">
          {tx.sectionTitle}
        </h2>
        <span
          className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-800 text-[11px] font-semibold uppercase tracking-wider"
          data-testid="player-rl-section-badge"
        >
          <ShieldCheck className="w-3 h-3" />{tx.sectionBadge}
        </span>
      </header>
      <p className="text-sm text-slate-600 mb-5" data-testid="player-rl-section-note">{tx.sectionNote}</p>

      {/* Tabs */}
      <div
        className="flex flex-wrap gap-1 border-b border-emerald-200 mb-4"
        role="tablist"
        data-testid="player-tabs"
      >
        {TABS.map(({ key, label, icon: Icon, testid }) => (
          <button
            key={key}
            type="button"
            role="tab"
            aria-selected={tab === key}
            onClick={() => setTab(key)}
            className={`inline-flex items-center gap-1.5 px-3 py-2 text-sm font-medium border-b-2 transition ${
              tab === key
                ? "border-emerald-600 text-emerald-800"
                : "border-transparent text-slate-600 hover:text-slate-900"
            }`}
            data-testid={testid}
          >
            <Icon className="w-4 h-4" /> {label}
          </button>
        ))}
      </div>

      {tab === "equipment" && (
        <EquipmentEditor tx={tx} rackets={rackets} setRackets={setRackets} />
      )}

      {tab === "qr" && (
        <div
          id="player-mi-qr"
          className="rounded-xl border border-slate-200 bg-white p-4 sm:p-6 flex flex-col sm:flex-row items-center gap-6 scroll-mt-24"
          data-testid="player-myqr-card"
        >
          <div className="shrink-0">
            <button
              type="button"
              onClick={() => setQrOpen(true)}
              className="block focus:outline-none focus:ring-2 focus:ring-emerald-400 rounded p-2 bg-white border border-slate-200 shadow-sm"
              data-testid="player-myqr-thumb"
            >
              <MockQrSvg size={180} />
            </button>
          </div>
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-1">
              <QrCode className="w-4 h-4 text-emerald-700" />
              <h3 className="font-semibold text-slate-900" data-testid="player-myqr-title">{tx.myQrTitle}</h3>
            </div>
            <p className="text-sm text-slate-700" data-testid="player-myqr-body">{tx.myQrBody}</p>
            <p className="text-xs text-slate-500 mt-2">{tx.myQrScanHint}</p>
            <button
              type="button"
              onClick={() => setQrOpen(true)}
              className="mt-4 px-3 py-2 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-semibold"
              data-testid="player-myqr-cta"
            >
              {tx.myQrCta}
            </button>
          </div>
        </div>
      )}

      {tab === "reservation" && (
        <div className="rounded-xl border border-slate-200 bg-white p-4 sm:p-6" data-testid="player-store-card">
          <ReservationBlock tx={tx} rackets={rackets} />
        </div>
      )}

      <p className="text-[11px] text-slate-500 mt-4 italic" data-testid="player-rl-section-legal">{tx.legalBlurb}</p>

      <QrModal open={qrOpen} onClose={() => setQrOpen(false)} tx={tx} seed="TEAM-PLAYER-DEMO" />
    </section>
  );
}
